import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '../config/firebase';

/**
 * Upload a user's profile photo
 * @param userId - The user's ID
 * @param file - The image file to upload
 * @returns The download URL of the uploaded photo
 */
export async function uploadProfilePhoto(userId: string, file: File): Promise<string> {
  // Validate file type
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image');
  }

  // Validate file size (5MB limit)
  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) {
    throw new Error('Image must be smaller than 5MB');
  }

  // Create a storage reference
  const timestamp = Date.now();
  const fileExtension = file.name.split('.').pop() || 'jpg';
  const fileName = `profile_${timestamp}.${fileExtension}`;
  const storageRef = ref(storage, `users/${userId}/profile/${fileName}`);

  try {
    // Upload the file
    const snapshot = await uploadBytes(storageRef, file, {
      contentType: file.type,
      customMetadata: {
        uploadedAt: new Date().toISOString(),
      },
    });

    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log('Profile photo uploaded successfully:', downloadURL);
    
    return downloadURL;
  } catch (error) {
    console.error('Failed to upload profile photo:', error);
    throw new Error('Failed to upload profile photo');
  }
}

/**
 * Download an image from a URL and upload it to Firebase Storage
 * Used for copying OAuth provider profile photos
 * @param userId - The user's ID
 * @param photoURL - The URL of the photo to download
 * @returns The download URL of the uploaded photo in Firebase Storage
 */
export async function downloadAndStoreProfilePhoto(userId: string, photoURL: string): Promise<string> {
  try {
    // Fetch the image from the URL
    const response = await fetch(photoURL);
    
    if (!response.ok) {
      throw new Error(`Failed to download image: ${response.statusText}`);
    }

    // Get the blob
    const blob = await response.blob();

    // Validate it's an image
    if (!blob.type.startsWith('image/')) {
      throw new Error('Downloaded file is not an image');
    }

    // Validate size (5MB limit)
    const maxSize = 5 * 1024 * 1024;
    if (blob.size > maxSize) {
      throw new Error('Downloaded image is too large (>5MB)');
    }

    // Create a storage reference
    const timestamp = Date.now();
    const fileExtension = blob.type.split('/').pop() || 'jpg';
    const fileName = `profile_oauth_${timestamp}.${fileExtension}`;
    const storageRef = ref(storage, `users/${userId}/profile/${fileName}`);

    // Upload the blob
    const snapshot = await uploadBytes(storageRef, blob, {
      contentType: blob.type,
      customMetadata: {
        uploadedAt: new Date().toISOString(),
        source: 'oauth_provider',
      },
    });

    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log('OAuth profile photo stored successfully:', downloadURL);
    
    return downloadURL;
  } catch (error) {
    console.error('Failed to download and store profile photo:', error);
    // Don't throw - just return the original URL as fallback
    console.warn('Falling back to original photo URL');
    return photoURL;
  }
}

/**
 * Delete a user's profile photo
 * @param photoURL - The download URL of the photo to delete
 */
export async function deleteProfilePhoto(photoURL: string): Promise<void> {
  try {
    // Extract the path from the URL
    // Firebase Storage URLs contain the path after '/o/'
    const urlObj = new URL(photoURL);
    const pathMatch = urlObj.pathname.match(/\/o\/(.+)/);
    
    if (!pathMatch) {
      throw new Error('Invalid Firebase Storage URL');
    }

    const path = decodeURIComponent(pathMatch[1]);
    const storageRef = ref(storage, path);

    await deleteObject(storageRef);
    console.log('Profile photo deleted successfully');
  } catch (error) {
    console.error('Failed to delete profile photo:', error);
    // Don't throw - deletion failures are not critical
  }
}
